import React, { useState, useEffect } from 'react';
import mfaService from '../../services/mfaService';
import { 
  Box, 
  Button, 
  TextField, 
  Typography, 
  Paper, 
  Container, 
  Alert,
  InputAdornment,
  IconButton,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

/**
 * Login Component
 * Provides a form for users to authenticate
 */
// Define global interface to expose login methods to DevTools
declare global {
  interface Window {
    _devLogin?: {
      setCredentials: (email: string, password: string) => void;
      submitForm: () => void;
    };
  }
}

const Login: React.FC = () => {
  // Empty credentials - users must enter their own
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [formError, setFormError] = useState('');
  const [mfaRequired, setMfaRequired] = useState(false);
  const [mfaUserId, setMfaUserId] = useState('');
  const [mfaToken, setMfaToken] = useState('');
  const [mfaError, setMfaError] = useState('');
  const { login, loading } = useAuth();
  const navigate = useNavigate();
  
  // Create form ref to access form methods
  const formRef = React.useRef<HTMLFormElement>(null);

  // Expose methods for DevTools to set login credentials
  useEffect(() => {
    // Create global interface for DevTools
    window._devLogin = {
      setCredentials: (devEmail: string, devPassword: string) => {
        setEmail(devEmail);
        setPassword(devPassword);
      },
      submitForm: () => {
        if (formRef.current) {
          formRef.current.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
        }
      }
    };
    
    return () => {
      // Clean up global object when component unmounts
      delete window._devLogin;
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');
    
    // Reset MFA state from any previous login attempts
    setMfaRequired(false);
    setMfaUserId('');
    setMfaToken('');
    setMfaError('');
    
    try {
      console.log('Login attempt with:', { email, password: password ? '***' : '' });
      const response = await login(email, password);
      console.log('Login response:', response);
      
      // Check if MFA is required
      if (response && response.mfaRequired && response.userId) {
        console.log('MFA required, showing dialog');
        setMfaRequired(true);
        setMfaUserId(response.userId);
        return;
      }
      
      console.log('No MFA required, redirecting to dashboard');
      // Redirect to dashboard after successful login
      navigate('/dashboard', { replace: true });
    } catch (err) {
      console.log('Login error:', err);
      setFormError(err instanceof Error ? err.message : 'Login failed. Please try again.');
    }
  };

  const handleMFASubmit = async () => {
    if (!mfaToken || mfaToken.length !== 6) {
      setMfaError('Please enter a valid 6-digit code');
      return;
    }

    try {
      setMfaError('');
      const response = await mfaService.validateMFA(mfaUserId, mfaToken);
      
      // Store token and redirect
      if (response.token) {
        localStorage.setItem('token', response.token);
        navigate('/dashboard', { replace: true });
        window.location.reload(); // Reload to update auth state
      }
    } catch (err) {
      setMfaError(err instanceof Error ? err.message : 'Invalid MFA code');
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <Container component="main" maxWidth="xs">
      <Paper 
        elevation={3} 
        sx={{ 
          p: 4, 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center',
          mt: 8
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
          <Typography 
            variant="h4" 
            component="h1" 
            sx={{ 
              color: '#003366', 
              fontWeight: 700,
              mb: 1
            }}
          >
            E-Unify
          </Typography>
          <Typography variant="h5" component="h2">
            Sign In
          </Typography>
        </Box>

        {formError && (
          <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
            {formError}
          </Alert>
        )}

        <Box component="form" ref={formRef} onSubmit={handleSubmit} noValidate sx={{ mt: 1, width: '100%' }}>
          <TextField
            margin="normal"
            required
            fullWidth
            id="email"
            label="Email Address"
            name="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            inputProps={{
              'aria-label': 'Email Address',
            }}
          />
          
          <TextField
            margin="normal"
            required
            fullWidth
            name="password"
            label="Password"
            type={showPassword ? 'text' : 'password'}
            id="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    onClick={togglePasswordVisibility}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            inputProps={{
              'aria-label': 'Password',
            }}
          />
          
          <Button
            type="submit"
            fullWidth
            variant="contained"
            color="primary"
            disabled={loading}
            sx={{ mt: 3, mb: 2 }}
          >
            {loading ? 'Signing In...' : 'Sign In'}
          </Button>
          
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <Link component={RouterLink} to="/register" variant="body2">
              {"Don't have an account? Sign Up"}
            </Link>
          </Box>
        </Box>
      </Paper>

      {/* MFA Dialog */}
      <Dialog open={mfaRequired} onClose={() => {}} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: '#003366', color: 'white' }}>
          Multi-Factor Authentication Required
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Enter the 6-digit code from your authenticator app:
          </Typography>
          {mfaError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {mfaError}
            </Alert>
          )}
          <TextField
            fullWidth
            label="MFA Code"
            value={mfaToken}
            onChange={(e) => {
              const value = e.target.value.replace(/\D/g, '').slice(0, 6);
              setMfaToken(value);
              setMfaError('');
            }}
            placeholder="123456"
            inputProps={{ maxLength: 6, pattern: '[0-9]*' }}
            autoFocus
            onKeyPress={(e) => {
              if (e.key === 'Enter' && mfaToken.length === 6) {
                handleMFASubmit();
              }
            }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Can't access your authenticator? Use a backup code instead.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setMfaRequired(false);
            setMfaToken('');
            setMfaError('');
          }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleMFASubmit}
            disabled={mfaToken.length !== 6 || loading}
            sx={{ bgcolor: '#003366' }}
          >
            {loading ? 'Verifying...' : 'Verify'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Login;
